import { getStatusClass, getPurchaseStatusClass } from '../lib/utils';
import { useTranslation } from '../contexts/TranslationContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

// Map status to translation key
const STATUS_KEYS = {
    'Quote': 'status.quote',
    'Quoted': 'status.quoted',
    'Draft': 'status.draft',
    'Submitted': 'status.submitted',
    'In Progress': 'status.inprogress',
    'On Hold': 'status.onhold',
    'Shipping': 'status.shipping',
    'Delivered/Closed': 'status.delivered',
    'Completed': 'status.completed',
    'Cancelled': 'status.cancelled',
};

/**
 * OrderStatusBadge — renders the coloured status pill.
 *
 * When status is "On Hold" and an ``onHoldReason`` is supplied, the
 * badge wraps in a tooltip so admins can see WHY the order is on hold
 * without opening the detail page. The reason itself is stored on
 * ``order.on_hold_reason`` (set when the admin transitioned to On Hold
 * with the mandatory reason field).
 */
export const OrderStatusBadge = ({ status, onHoldReason, onHoldAt, onHoldBy }) => {
    const { t } = useTranslation();
    const translationKey = STATUS_KEYS[status] || `status.${status.toLowerCase().replace(/\s+/g, '')}`;

    const badge = (
        <span className={`status-badge ${getStatusClass(status)}`} data-testid="order-status-badge">
            {t(translationKey, status)}
        </span>
    );

    if (status !== 'On Hold' || !onHoldReason) {
        return badge;
    }

    return (
        <TooltipProvider delayDuration={150}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="inline-block cursor-help" data-testid="on-hold-badge-with-reason">
                        {badge}
                    </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs" side="top">
                    <div className="font-medium mb-1">On hold reason:</div>
                    <div className="whitespace-pre-wrap">{onHoldReason}</div>
                    {(onHoldAt || onHoldBy?.name) && (
                        <div className="mt-1.5 pt-1.5 border-t border-white/10 text-[10px] opacity-80">
                            {onHoldBy?.name && <span>by {onHoldBy.name}</span>}
                            {onHoldAt && (
                                <span>{onHoldBy?.name ? ' · ' : ''}{new Date(onHoldAt).toLocaleString()}</span>
                            )}
                        </div>
                    )}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export const PurchaseStatusBadge = ({ status }) => {
    const { t } = useTranslation();
    const translationKey = `purchase.status.${status.toLowerCase().replace(/\s+/g, '')}`;
    
    return (
        <span className={`status-badge ${getPurchaseStatusClass(status)}`} data-testid="purchase-status-badge">
            {t(translationKey, status)}
        </span>
    );
};

// Finance/Invoicing status badge - shows internal e-conomic invoice state (admin-only)
const FINANCE_STATUS_CONFIG = {
    'draft_in_economic': { label: 'Invoicing', className: 'bg-violet-100 text-violet-700 border border-violet-200' },
    'booked_in_economic': { label: 'Invoiced', className: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
    'local_draft': { label: 'Invoice Draft', className: 'bg-slate-100 text-slate-600 border border-slate-200' },
};

export const FinanceStatusBadge = ({ invoiceEconomicStatus, invoiceStatus }) => {
    // Don't show anything if there's no invoice at all
    if (!invoiceStatus && !invoiceEconomicStatus) return null;
    
    const config = FINANCE_STATUS_CONFIG[invoiceEconomicStatus];
    
    // For orders with an invoice but no economic_status yet
    if (!config && invoiceStatus) {
        // ready_to_book = "Ready to Invoice"
        if (invoiceStatus === 'ready_to_book') {
            return (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 border border-blue-200" data-testid="finance-status-badge">
                    Ready to Invoice
                </span>
            );
        }
        return null;
    }
    
    if (!config) return null;
    
    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${config.className}`} data-testid="finance-status-badge">
            {config.label}
        </span>
    );
};

// Invoice payment status badge (from e-conomic sync)
const PAYMENT_STATUS_CONFIG = {
    'paid': { label: 'Paid', className: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
    'unpaid': { label: 'Unpaid', className: 'bg-amber-100 text-amber-700 border border-amber-200' },
    'overdue': { label: 'Overdue', className: 'bg-red-100 text-red-700 border border-red-200' },
};

export const InvoicePaymentBadge = ({ status }) => {
    if (!status) return null;
    const config = PAYMENT_STATUS_CONFIG[status];
    if (!config) return null;
    
    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${config.className}`} data-testid="invoice-payment-badge">
            {config.label}
        </span>
    );
};


// Test Press status badge (workflow-driven flag, primarily vinyl).
// `not_required` and `null` render nothing so we don't clutter the
// orders table for every disc / CD order that will never need one.
// Legacy aliases (``shipped`` / ``received_by_customer``) rewritten
// by the state-machine migration but kept here for a warm SPA cache.
const TEST_PRESS_CONFIG = {
    // ``required`` is the state the order lands in the moment the
    // customer submits a configuration that includes purchased Test
    // Pressings. Admin and customer surfaces both display it as
    // "Ordered" so the Test Press section is visible from day 1 —
    // the admin's "PO not yet sent" nuance is tracked via the
    // supplier confirmation workflow, not this chip.
    'required':             { label: 'TP: Ordered',       className: 'bg-blue-100 text-blue-700 border border-blue-200' },
    // Once the Sony PO has actually been sent (``ordered`` state in
    // the TP state machine) the chip flips to "In production" so
    // admins can tell at a glance which TPs are still on the
    // internal to-do pile vs. already pressing at the plant.
    'ordered':              { label: 'TP: In production', className: 'bg-sky-100 text-sky-700 border border-sky-200' },
    'confirmed':            { label: 'TP: Confirmed',  className: 'bg-sky-100 text-sky-700 border border-sky-200' },
    'dispatched':           { label: 'TP: Dispatched', className: 'bg-indigo-100 text-indigo-700 border border-indigo-200' },
    // Per Test Press spec, admin overview chip reads "TP: Del." once
    // FLOW has sent the customer the TP delivery notice. Driven by
    // the SUPPLIER_TP_DELIVERED event in the state machine — NOT by
    // the production-order DSV carrier tracking.
    'arrived':              { label: 'TP: Del.',       className: 'bg-amber-100 text-amber-700 border border-amber-200' },
    'approved':             { label: 'TP: Approved',   className: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
    'rejected':             { label: 'TP: Rejected',   className: 'bg-red-100 text-red-700 border border-red-200' },
    // Phase 3: admin has forwarded the customer's rejection to Sony
    // and is now waiting for Sony to respond. Customer sees a parallel
    // "Awaiting Sony" chip so they understand the ball is no longer
    // in their court.
    'awaiting_feedback':    { label: 'TP: Awaiting Sony', className: 'bg-sky-100 text-sky-700 border border-sky-200' },
    // Legacy aliases — new writes use the canonical keys above.
    'shipped':              { label: 'TP: Dispatched', className: 'bg-indigo-100 text-indigo-700 border border-indigo-200' },
    'received_by_customer': { label: 'TP: Arrived',    className: 'bg-amber-100 text-amber-700 border border-amber-200' },
};

// Customer-facing full-word labels — kept distinct from the admin
// "TP: *" compact chips so customers see a plain-English secondary
// badge on My Orders.
//
// Internal/external boundary note: ``awaiting_feedback`` is an
// INTERNAL workflow state ("OwnTune is coordinating with the pressing
// plant"). The customer must never see Sony / supplier wording — the
// customer deals with OwnTune, not with our production partner. We
// surface it as a neutral "Processing" chip here and the TP card
// shows reassurance copy instead of the "Feedback sent to Sony"
// admin panel. Admins keep the "TP: Awaiting Sony" chip in the admin
// path below.
const TEST_PRESS_CUSTOMER_LABEL = {
    'required':   'Test Press: Ordered',
    'ordered':    'Test Press: In production',
    'confirmed':  'Test Press: Confirmed',
    'dispatched': 'Test Press: Confirmed',
    'arrived':    'Test Press: Review',
    'rejected':   'Test Press: Rejected',
    'awaiting_feedback': 'Test Press: Processing',
    // ``approved`` is retained on My Orders as a small positive
    // secondary chip so the customer can see at a glance that the
    // TP step is complete. The Delivery column already drops the TP
    // expected date once we reach ``approved`` (it switches back to
    // the production delivery milestone), so there's no stale date
    // riding alongside the chip.
    'approved':   'Test Press: Approved',
    // Legacy aliases
    'shipped':              'Test Press: Confirmed',
    'received_by_customer': 'Test Press: Review',
};

export const TestPressBadge = ({ status, expectedArrival, viewerRole = 'admin', sonyResponseBranch = null, tpVersion = 1 }) => {
    const { t } = useTranslation();
    if (!status || status === 'not_required') return null;
    const isCustomer = viewerRole === 'customer';

    // ``expectedArrival`` is intentionally NOT rendered on the badge
    // anymore — the orders list's Delivery column surfaces that date
    // directly while the TP is active (and swaps to the final
    // production date once the TP is approved). The parameter is
    // kept for API compatibility (tooltip + legacy callers).
    void expectedArrival;

    // Customer path: full-word "Test Press: *" labels; hide once
    // the workflow has reached ``approved`` so completed orders
    // don't carry a stale badge on My Orders.
    if (isCustomer) {
        // Phase 4 "New Test Press (V2)" branch starts a brand-new
        // TP supplier cycle from REQUIRED. We flag the whole
        // pre-arrival range (required/ordered/confirmed/dispatched)
        // as "New test press in production" so the customer gets
        // the same reassurance across the full V2 cycle until it
        // physically arrives. ``tpVersion`` guards against stale
        // ``sony_response_branch`` reads on legacy data.
        const isV2CycleActive = (
            Number(tpVersion || 1) > 1
            && sonyResponseBranch === 'new_tp_v2'
            && ['required', 'ordered', 'confirmed', 'dispatched'].includes(status)
        );
        // Translation Center-aware customer labels. Falls back to
        // the English source map so missing keys never break the UI.
        const customerLabelKey = isV2CycleActive
            ? 'tp.customer.new_in_production'
            : `tp.customer.${status}`;
        const customerLabelFallback = isV2CycleActive
            ? 'Test Press: New test press in production'
            : TEST_PRESS_CUSTOMER_LABEL[status];
        if (!customerLabelFallback) return null;
        const customerLabel = t(customerLabelKey, customerLabelFallback);
        const needsReview = status === 'arrived';
        const className = needsReview
            ? 'bg-amber-100 text-amber-800 border border-amber-300 font-semibold'
            : status === 'rejected'
                ? 'bg-red-100 text-red-700 border border-red-200'
                : status === 'awaiting_feedback'
                    ? 'bg-slate-100 text-slate-700 border border-slate-200'
                    : status === 'approved'
                        ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                        : (status === 'ordered' || status === 'required')
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'bg-sky-100 text-sky-700 border border-sky-200';
        return (
            <span
                className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${className}`}
                data-testid={needsReview ? 'tp-badge-review' : 'tp-badge'}
                title={
                    needsReview
                        ? t('tp.customer.review_tooltip', 'Your test press has been delivered — please approve or reject it inside the order.')
                        : t('tp.customer.generic_tooltip', 'Test press status: {status}', { status: customerLabel.replace(/^Test Press:\s*/i, '') })
                }
            >
                {customerLabel}
            </span>
        );
    }

    // Admin path: compact "TP: *" chip. The expected date lives in
    // the Delivery column — no second surface here to keep the list
    // scannable.
    const config = TEST_PRESS_CONFIG[status];
    if (!config) return null;
    const adminLabel = t(`tp.admin.${status}`, config.label);
    return (
        <span
            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${config.className}`}
            data-testid="tp-badge"
            title={t('tp.admin.generic_tooltip', 'Test press status: {status}', { status: adminLabel.replace(/^TP:\s*/i, '') })}
        >
            {adminLabel}
        </span>
    );
};

// Full label map for the detail-page dropdown (no "TP:" prefix).
// Legacy statuses (``shipped`` / ``received_by_customer``) are kept
// as aliases so any old data / cached SPA can still render — new
// writes use the canonical state-machine names.
//
// NOTE: This map is the ENGLISH source of truth. For localised
// rendering use ``useTpStatusLabel()`` below, which routes every
// value through the Translation Center under the ``tp.status.*``
// key family (keeps admins able to edit the Danish/other labels
// without touching code).
export const TEST_PRESS_STATUS_LABELS = {
    'not_required':         'Not required',
    // Internal state ``required`` (customer configured TP, SC not
    // yet created) is displayed as "Ordered". Once the admin sends
    // the Sony PO the state advances to ``ordered`` and the chip
    // flips to "In production" so the two phases are visibly
    // distinct in both admin and customer surfaces.
    'required':             'Ordered',
    'ordered':              'In production',
    'confirmed':            'Confirmed',
    'dispatched':           'Dispatched',
    'arrived':              'Delivered',
    'approved':             'Approved',
    'rejected':             'Rejected',
    'awaiting_feedback':    'Awaiting Sony',
    // Legacy aliases — safe to remove after all frontends refresh:
    'shipped':              'Dispatched',
    'received_by_customer': 'Delivered',
};

// Translation Center-aware label lookup for Test Press statuses. One
// stable key per canonical status so admins can rename them per
// language without touching the English source map above.
export const useTpStatusLabel = () => {
    const { t } = useTranslation();
    return (status) => {
        if (!status) return '';
        const fallback = TEST_PRESS_STATUS_LABELS[status] || status;
        return t(`tp.status.${status}`, fallback);
    };
};
